import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar, FormControl, IconButton, InputAdornment, MenuItem, Select,
  TextField, ToggleButton, ToggleButtonGroup, alpha, useMediaQuery,
} from '@mui/material';
import { ArrowBack, ArrowForward, Search, SportsSoccer, TrendingUp } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../context/AppContext';
import { useClasificacion, useJornadas } from '../hooks/queries';
import { completeVenueStandings } from '../utils/classificationCoverage';
import { Button, EmptyState, Skeleton } from '../design-system/components';
import Filters from '../components/Filters';
import { ClassificationTrend } from '../components/dashboardV2/MatchdayCharts';
import usePageTitle from '../hooks/usePageTitle';
import './PartidosPageModern.css';
import './ClasificacionPageModern.css';

export const getPositionHighlight = (theme, position, total) => {
  if (position <= 4) return {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
    borderColor: theme.palette.primary.main,
  };
  if (position >= total - 2) return {
    backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
    borderColor: theme.palette.error.main,
  };
  return { backgroundColor: 'transparent', borderColor: 'transparent' };
};

const numericStat = (team, keys) => {
  for (const key of keys) {
    const raw = team?.[key];
    if (raw === null || raw === undefined || String(raw).trim() === '' || typeof raw === 'boolean') continue;
    const value = Number(raw);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const normalizeFormResult = (result) => {
  const value = String(result || '').trim().toUpperCase();
  if (['G', 'V', 'W'].includes(value)) return 'V';
  if (['E', 'D'].includes(value)) return 'E';
  if (['P', 'L'].includes(value)) return 'P';
  return null;
};

const getVenueStreak = (team, suffix) => [1, 2, 3, 4, 5]
  .map((index) => team?.[`racha_${index}_${suffix}`] ?? team?.[`racha_${index}_${suffix.toLowerCase()}`])
  .map(normalizeFormResult).filter(Boolean);

export const getClasificacionByScope = (clasificacion = [], scope = 'all') => {
  if (!Array.isArray(clasificacion)) return [];
  if (scope === 'all') return clasificacion;
  const suffix = scope === 'home' ? 'Casa' : 'Fuera';
  const snake = scope === 'home' ? 'casa' : 'fuera';
  return clasificacion.map((team) => {
    const ganados = numericStat(team, [`ganados${suffix}`, `ganados_${snake}`]);
    const empatados = numericStat(team, [`empatados${suffix}`, `empatados_${snake}`]);
    return {
      ...team,
      jugados: numericStat(team, [`jugados${suffix}`, `jugados_${snake}`]),
      ganados, empatados,
      perdidos: numericStat(team, [`perdidos${suffix}`, `perdidos_${snake}`]),
      golesFavor: numericStat(team, [`golesFavor${suffix}`, `golesfavor${suffix}`, `goles_a_favor_${snake}`]),
      golesContra: numericStat(team, [`golesContra${suffix}`, `golescontra${suffix}`, `goles_en_contra_${snake}`]),
      puntos: ganados === null || empatados === null ? null : ganados * 3 + empatados,
      ultimos5: getVenueStreak(team, suffix),
    };
  }).sort((a, b) => {
    if (a.puntos === null || b.puntos === null) return (a.puntos === null) - (b.puntos === null);
    return b.puntos - a.puntos ||
      ((b.golesFavor ?? 0) - (b.golesContra ?? 0)) - ((a.golesFavor ?? 0) - (a.golesContra ?? 0)) ||
      (b.golesFavor ?? 0) - (a.golesFavor ?? 0) ||
      String(a.nombre || a.nombreequipo || '').localeCompare(String(b.nombre || b.nombreequipo || ''), 'es');
  });
};

export const getClasificacionJornadaNumber = (jornada, index) => {
  const raw = jornada?.jornada ?? jornada?.numJornada ?? jornada?.numeroJornada;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const match = typeof raw === 'string' && raw.match(/\d+/);
  return match ? Number(match[0]) : index + 1;
};

export const orderClasificacionesByJornada = (clasificaciones = []) => Array.isArray(clasificaciones)
  ? clasificaciones.map((clasificacion, index) => ({
    clasificacion, jornadaNumber: getClasificacionJornadaNumber(clasificacion, index),
  })).sort((a, b) => a.jornadaNumber - b.jornadaNumber) : [];

export const getLatestClasificacionJornada = (clasificaciones = []) => {
  const ordered = orderClasificacionesByJornada(clasificaciones);
  return [...ordered].reverse().find(({ clasificacion }) => clasificacion.clasificacion?.length)?.jornadaNumber
    ?? ordered.at(-1)?.jornadaNumber ?? null;
};

const positionOf = (team, index, scope, complete) => scope === 'all'
  ? numericStat(team, ['posicion']) ?? index + 1 : complete ? index + 1 : null;

export const buildClassificationTrend = (ordered, teamId, scope, selectedRound) => {
  const included = ordered.filter(({ jornadaNumber }) => jornadaNumber <= selectedRound);
  if (!included.length) return { rounds: [], groupSize: 0 };
  const snapshots = new Map(included.map(({ jornadaNumber, clasificacion }) => {
    const teams = getClasificacionByScope(clasificacion.clasificacion, scope);
    const index = teams.findIndex((team) => team.codequipo != null && String(team.codequipo) === String(teamId));
    const complete = teams.every((team) => ['puntos', 'golesFavor', 'golesContra']
      .every((key) => numericStat(team, [key]) !== null));
    return [jornadaNumber, { size: teams.length,
      position: index < 0 ? null : positionOf(teams[index], index, scope, complete) }];
  }));
  const start = included[0].jornadaNumber;
  const end = included.at(-1).jornadaNumber;
  return {
    groupSize: Math.max(...[...snapshots.values()].map((snapshot) => snapshot.size)),
    rounds: Array.from({ length: end - start + 1 }, (_, index) => ({
      round: start + index, position: snapshots.get(start + index)?.position ?? null,
    })),
  };
};

function FormStreak({ team, scope }) {
  const { t } = useTranslation('classifications');
  const results = Array.isArray(team.ultimos5) && team.ultimos5.length ? team.ultimos5
    : scope === 'all' ? [1, 2, 3, 4, 5].map((index) => team[`racha_${index}`]) : [];
  const played = numericStat(team, ['jugados']);
  const recent = (Number.isInteger(played) && played >= 0 && played < 5
    ? results.slice(0, played) : results).slice(-5);
  if (!recent.some((result) => normalizeFormResult(result))) return <span className="cs-muted">—</span>;
  return <span className="cs-form" aria-label={t('table.form')} title={t('form.order')}>
    {recent.map((result, index) => {
      const value = normalizeFormResult(result);
      const key = value === 'V' ? 'win' : value === 'E' ? 'draw' : value === 'P' ? 'loss' : 'unknown';
      const latest = index === recent.length - 1;
      return <span key={index} className={`cs-form-${value || 'unknown'}`} data-latest={latest || undefined}
        title={`${t(`form.${key}`)}${latest ? ` · ${t('form.latest')}` : ''}`}
        aria-description={latest ? t('form.latest') : undefined}
        aria-label={t(`form.${key}`)}>{t(`form.${key}Short`)}</span>;
    })}
  </span>;
}

const statKeys = {
  matchesPlayed: ['jugados'], won: ['ganados'], drawn: ['empatados'], lost: ['perdidos'],
  goalsFor: ['golesFavor', 'golesfavor'], goalsAgainst: ['golesContra', 'golescontra'], points: ['puntos'],
};
const columns = ['matchesPlayed', 'won', 'drawn', 'lost', 'goalsFor', 'goalsAgainst', 'goalDifference', 'points'];
const stat = (team, column) => {
  if (column !== 'goalDifference') return numericStat(team, statKeys[column]);
  const scored = numericStat(team, statKeys.goalsFor);
  const conceded = numericStat(team, statKeys.goalsAgainst);
  return scored === null || conceded === null ? null : scored - conceded;
};
const displayStat = (team, column) => {
  const value = stat(team, column);
  return value === null ? '—' : column === 'goalDifference' && value > 0 ? `+${value}` : value;
};
const nameOf = (team) => team.nombre || team.nombreequipo || '—';

export default function ClasificacionPage({ favorite }) {
  usePageTitle('Clasificación');
  const { t } = useTranslation('classifications');
  const navigate = useNavigate();
  const { filters } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = ['home', 'away'].includes(searchParams.get('scope')) ? searchParams.get('scope') : 'all';
  const search = searchParams.get('q') || '';
  const isMobile = useMediaQuery('(max-width: 699px)');
  const favoriteLink = useRef(null);
  const [locateRequest, setLocateRequest] = useState(0);
  const { data, isLoading, isFetching, error, refetch } = useClasificacion(filters.temporada, filters.competicion, filters.grupo);
  const { data: calendar, isFetching: calendarFetching, error: calendarError, refetch: refetchCalendar } =
    useJornadas(filters.grupo, filters.temporada, {
      enabled: !!(filters.grupo && filters.temporada && scope !== 'all'),
    });
  const enriched = useMemo(() => completeVenueStandings(data, calendar), [data, calendar]);
  const ordered = useMemo(() => orderClasificacionesByJornada(enriched), [enriched]);
  const selectedRound = Number(searchParams.get('jornada')) || getLatestClasificacionJornada(data);
  const filtersKey = `${filters.temporada || ''}:${filters.competicion || ''}:${filters.grupo || ''}`;
  const previousFilters = useRef(filtersKey);
  useEffect(() => {
    if (previousFilters.current === filtersKey) return;
    previousFilters.current = filtersKey;
    setSearchParams({}, { replace: true });
  }, [filtersKey, setSearchParams]);
  const setControl = (key, value) => setSearchParams((previous) => {
    const next = new URLSearchParams(previous);
    if (value) next.set(key, String(value));
    else next.delete(key);
    if (key === 'team' && next.get('compare') === String(value)) next.delete('compare');
    return next;
  }, { replace: true });

  const current = ordered.find(({ jornadaNumber }) => jornadaNumber === selectedRound)?.clasificacion?.clasificacion;
  const teams = useMemo(() => getClasificacionByScope(current, scope), [current, scope]);
  const complete = teams.every((team) => ['puntos', 'golesFavor', 'golesContra']
    .every((key) => numericStat(team, [key]) !== null));
  const ranked = teams.map((team, index) => ({ team, position: positionOf(team, index, scope, complete) }));
  const previousPositions = useMemo(() => {
    const previous = ordered.find(({ jornadaNumber }) => jornadaNumber === selectedRound - 1);
    const previousTeams = getClasificacionByScope(previous?.clasificacion.clasificacion, scope);
    const previousComplete = previousTeams.every((team) => ['puntos', 'golesFavor', 'golesContra']
      .every((key) => numericStat(team, [key]) !== null));
    return new Map(previousTeams.flatMap((team, index) => team.codequipo == null ? []
      : [[String(team.codequipo), positionOf(team, index, scope, previousComplete)]]));
  }, [ordered, selectedRound, scope]);
  const visible = ranked.filter(({ team }) => nameOf(team).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const favoriteId = favorite?.idEquipo != null &&
    String(favorite.idTemporada) === String(filters.temporada) &&
    String(favorite.idCompeticion) === String(filters.competicion) &&
    String(favorite.idGrupo) === String(filters.grupo) ? String(favorite.idEquipo) : null;
  const own = ranked.find(({ team }) => team.codequipo != null && String(team.codequipo) === favoriteId);
  const comparisonTeams = useMemo(() => {
    const byId = new Map();
    ordered.filter(({ jornadaNumber }) => jornadaNumber <= selectedRound).forEach(({ clasificacion }) => {
      clasificacion.clasificacion?.forEach((team) => {
        if (team.codequipo != null) byId.set(String(team.codequipo), team);
      });
    });
    return [...byId.values()].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }, [ordered, selectedRound]);
  const primaryId = searchParams.get('team') ?? favoriteId;
  const comparisonId = searchParams.get('compare');
  const primary = comparisonTeams.find((team) => String(team.codequipo) === primaryId);
  const secondary = comparisonId !== primaryId
    ? comparisonTeams.find((team) => String(team.codequipo) === comparisonId) : undefined;
  const history = useMemo(() => primary
    ? buildClassificationTrend(ordered, primary.codequipo, scope, selectedRound) : null,
  [ordered, primary, scope, selectedRound]);
  const comparisonHistory = useMemo(() => primary && secondary
    ? buildClassificationTrend(ordered, secondary.codequipo, scope, selectedRound) : null,
  [ordered, primary, secondary, scope, selectedRound]);
  const ownIndex = ranked.indexOf(own);
  const neighbors = own?.position != null ? [
    { row: ranked[ownIndex - 1], direction: 'above' },
    { row: ranked[ownIndex + 1], direction: 'below' },
  ].filter(({ row }) => row) : [];
  const gap = own && teams.every((team) => numericStat(team, ['puntos']) !== null)
    ? numericStat(teams[0], ['puntos']) - numericStat(own.team, ['puntos']) : null;
  const loading = isLoading && !data;
  const canOpenTeam = !!(filters.temporada && filters.competicion && filters.grupo);
  const movement = (team, position) => {
    const previous = team.codequipo == null ? null : previousPositions.get(String(team.codequipo));
    const change = position == null || previous == null ? null : previous - position;
    const direction = change === null ? 'unavailable' : change > 0 ? 'up' : change < 0 ? 'down' : 'same';
    const label = t(`movement.${direction}`, { count: Math.abs(change ?? 0) });
    return <span className={`cs-movement cs-movement-${direction}`} aria-label={label} title={label}>
      {change === null ? '—' : change === 0 ? '=' : `${change > 0 ? '↑' : '↓'} ${Math.abs(change)}`}
    </span>;
  };

  useEffect(() => {
    if (!locateRequest || !favoriteLink.current) return;
    favoriteLink.current.scrollIntoView?.({ block: 'center' });
    favoriteLink.current.focus({ preventScroll: true });
    setLocateRequest(0);
  }, [locateRequest, search, selectedRound, scope]);

  const openTeam = (team) => {
    if (!canOpenTeam || team.codequipo == null) return;
    navigate(`/estadisticasEquipos?id=${encodeURIComponent(`${team.codequipo}_${filters.competicion}_${filters.grupo}_${filters.temporada}`)}`,
      { state: { equipo: team } });
  };
  const identity = (team) => <button type="button" className="cs-team-link"
    disabled={!canOpenTeam || team.codequipo == null}
    ref={team.codequipo != null && String(team.codequipo) === favoriteId ? favoriteLink : undefined}
    onClick={() => openTeam(team)}>
    <Avatar className="cs-crest" src={team.escudo || team.escudoEquipo || undefined} alt="" aria-hidden="true">
      {nameOf(team).split(/\s+/).map((word) => word[0]).slice(0, 2).join('')}
    </Avatar><span>{nameOf(team)}</span>
  </button>;
  const favoriteRow = (team) => team.codequipo != null && String(team.codequipo) === favoriteId;
  const openMatches = () => navigate(selectedRound ? `/partidos?jornada=${selectedRound}` : '/partidos');

  return <div className="pm-page cs-page">
    <header className="pm-heading">
      <div><h1>{t('pageTitle')}</h1><p>{t('subtitle')}</p></div>
      <Button variant="contained" startIcon={<SportsSoccer />} onClick={openMatches}>{t('viewMatches')}</Button>
    </header>
    <div className="pm-context-line">
      <IconButton aria-label={t('buttons.back', { ns: 'common', defaultValue: 'Volver' })}
        onClick={() => navigate(window.history.state?.idx > 0 ? -1 : '/dashboard')}><ArrowBack fontSize="small" /></IconButton>
      <strong>{t('pageTitle')}</strong>
      <span>{[filters.competicionNombre, filters.grupoNombre].filter(Boolean).join(' · ')}</span>
    </div>
    <section className="pm-filter-panel" aria-label={t('competitionContext')}>
      <Filters variant="workspace" onApplyFilters={() =>
        Promise.all([refetch(), scope !== 'all' ? refetchCalendar() : undefined])} />
      <div className="pm-filter-hint"><span>{t('filterHint')}</span><span>{t('navigationHint')}</span></div>
    </section>
    <div className="cs-controls">
      <div><label htmlFor="cs-search">{t('searchLabel')}</label>
        <TextField id="cs-search" fullWidth size="small" value={search}
          onChange={(event) => setControl('q', event.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </div>
      <ToggleButtonGroup exclusive value={scope} aria-label={t('filters.scopeLabel')}
        onChange={(_, value) => { if (value) setControl('scope', value); }}>
        {['all', 'home', 'away'].map((value) => <ToggleButton key={value} value={value}>{t(`filters.${value}`)}</ToggleButton>)}
      </ToggleButtonGroup>
      <div><label id="cs-matchday-label">{t('filters.matchday')}</label>
        <FormControl size="small" fullWidth>
          <Select labelId="cs-matchday-label" value={ordered.some(({ jornadaNumber }) => jornadaNumber === selectedRound) ? selectedRound : ''}
            onChange={(event) => setControl('jornada', event.target.value)}>
            {ordered.map(({ jornadaNumber }) => <MenuItem key={jornadaNumber} value={jornadaNumber}>
              {t('matchdayLabel', { number: jornadaNumber })}</MenuItem>)}
          </Select>
        </FormControl>
      </div>
      <Button disabled={!own || !canOpenTeam} variant="text" startIcon={<Search />}
        title={!own ? t('favoriteUnavailable') : undefined}
        onClick={() => { setControl('q', null); setLocateRequest((previous) => previous + 1); }}>{t('locateFavorite')}</Button>
    </div>
    {error && <EmptyState type="error" onAction={refetch} actionLabel={t('errors.retry')} />}
    {loading && <Skeleton type="table" rows={10} />}
    {!loading && !error && ordered.length === 0 && <EmptyState type="no-data" title={t('empty.noData')} description={t('empty.noDataDescription')} />}
    {!loading && ordered.length > 0 && <div className="cs-layout">
      <section className="cs-table-panel" aria-label={t('pageTitle')}>
        <header><h2>{t('cardTitle', { number: selectedRound })}</h2>
          <p role="status">{isFetching ? t('refreshing') : t('teamCount', { visible: visible.length, total: teams.length })}</p></header>
        <p className="cs-form-guide">{t('form.order')}</p>
        {scope !== 'all' && <p className="cs-scope-note">{t(calendarFetching && !complete
          ? 'calendarLoading' : complete ? 'venueRankingNote' : 'partialScope')}</p>}
        {scope !== 'all' && calendarError && <p role="alert" className="cs-scope-note">{t('calendarError')}{' '}
          <Button variant="text" onClick={refetchCalendar}>{t('errors.retry')}</Button></p>}
        {!teams.length ? <p className="cs-empty">{t('empty.noTeams')}</p>
          : !visible.length ? <div className="cs-empty"><p>{t('empty.noSearch')}</p><Button onClick={() => setControl('q', null)}>{t('clearSearch')}</Button></div>
            : isMobile ? <ol className="cs-mobile-list" aria-label={t('pageTitle')}>
              {visible.map(({ team, position }, index) => <li key={team.codequipo ?? index}
                className={favoriteRow(team) ? 'cs-favorite-row' : undefined} value={position ?? undefined}>
                <div className="cs-mobile-main"><span className="cs-rank">{position ?? '—'}</span>{identity(team)}
                  <strong className="cs-points" aria-label={`${t('table.points')}: ${displayStat(team, 'points')}`}>{displayStat(team, 'points')}</strong></div>
                <dl className="cs-mobile-stats">{columns.filter((column) => column !== 'points').map((column) =>
                  <div key={column}><dt>{t(`table.${column}`)}</dt><dd>{displayStat(team, column)}</dd></div>)}</dl>
                <div className="cs-mobile-form"><span>{t('movement.short')} {movement(team, position)}</span>
                  <FormStreak team={team} scope={scope} />
                  {favoriteRow(team) && <span className="cs-own-label">{t('yourTeam')}</span>}</div>
              </li>)}
            </ol> : <div className="cs-table-scroll"><table>
              <caption className="cs-sr-only">{t('cardTitle', { number: selectedRound })}</caption>
              <thead><tr><th scope="col">{t('table.position')}</th><th scope="col">{t('table.team')}</th>
                {columns.map((column) => <th className="cs-number" scope="col" key={column}>{t(`table.${column}`)}</th>)}
                <th scope="col" title={t('movement.title')}>{t('movement.short')}</th>
                <th scope="col">{t('table.form')}</th></tr></thead>
              <tbody>{visible.map(({ team, position }, index) => <tr key={team.codequipo ?? index}
                className={favoriteRow(team) ? 'cs-favorite-row' : undefined}>
                <td className="cs-rank">{position ?? '—'}</td><th scope="row">{identity(team)}</th>
                {columns.map((column) => <td key={column} className={`cs-number${column === 'points' ? ' cs-points' : ''}`}>{displayStat(team, column)}</td>)}
                <td>{movement(team, position)}</td>
                <td><FormStreak team={team} scope={scope} /></td>
              </tr>)}</tbody>
            </table></div>}
        <footer>{t('formLegend')}</footer>
      </section>
      <aside className="cs-aside">
        <section className="cs-favorite-summary" aria-label={t('yourTeam')}>
          <p className="cs-muted">{t('yourTeam')}</p>
          {own ? <><h2>{nameOf(own.team)}</h2>
            <dl className="cs-kpis"><div><dd>{own.position ?? '—'}</dd><dt>{t('table.position')}</dt></div>
              <div><dd>{displayStat(own.team, 'points')}</dd><dt>{t('table.points')}</dt></div></dl>
            {gap !== null && <p className="cs-gap">{t(own.position === 1 ? 'groupLeader' : gap === 0 ? 'levelWithLeader' : 'leaderGap', { count: gap })}</p>}
            <p className="cs-change-summary">{t('movement.title')} {movement(own.team, own.position)}</p>
            {neighbors.length > 0 && <section className="cs-neighbors" aria-label={t('neighbors.title')}>
              <h3>{t('neighbors.title')}</h3>
              <dl>{neighbors.map(({ row, direction }) => {
                const ownPoints = stat(own.team, 'points');
                const otherPoints = stat(row.team, 'points');
                const difference = ownPoints === null || otherPoints === null ? null
                  : direction === 'above' ? otherPoints - ownPoints : ownPoints - otherPoints;
                return <div key={direction}><dt>{t(`neighbors.${direction}`)} · {nameOf(row.team)}</dt>
                  <dd>{difference === null || difference < 0 ? '—' : difference === 0
                    ? t('neighbors.level')
                    : t('neighbors.points', { count: difference })}</dd></div>;
              })}</dl>
            </section>}
            <FormStreak team={own.team} scope={scope} />
            <Button variant="text" startIcon={<TrendingUp />} disabled={!canOpenTeam} onClick={() => openTeam(own.team)}>{t('teamStats')}</Button>
          </> : <p>{t('favoriteUnavailable')}</p>}
        </section>
        {comparisonTeams.length > 0 && <section className="cs-history dv-panel">
          <h2>{t('historyTitle')}</h2><p className="cs-muted">{t('historyScope', { scope: t(`filters.${scope}`) })}</p>
          <div className="cs-compare-controls">
            <div><label id="cs-primary-label">{t('comparison.primary')}</label>
              <FormControl fullWidth size="small"><Select labelId="cs-primary-label"
                value={primary ? String(primary.codequipo) : ''} displayEmpty
                onChange={(event) => setControl('team', event.target.value)}>
                <MenuItem value="" disabled>{t('comparison.choose')}</MenuItem>
                {comparisonTeams.map((team) => <MenuItem key={team.codequipo} value={String(team.codequipo)}>{nameOf(team)}</MenuItem>)}
              </Select></FormControl>
            </div>
            <div><label id="cs-compare-label">{t('comparison.secondary')}</label>
              <FormControl fullWidth size="small"><Select labelId="cs-compare-label"
                value={secondary ? String(secondary.codequipo) : ''} displayEmpty disabled={!primary}
                onChange={(event) => setControl('compare', event.target.value)}>
                <MenuItem value="">{t('comparison.none')}</MenuItem>
                {comparisonTeams.filter((team) => String(team.codequipo) !== primaryId).map((team) =>
                  <MenuItem key={team.codequipo} value={String(team.codequipo)}>{nameOf(team)}</MenuItem>)}
              </Select></FormControl>
            </div>
          </div>
          {history && (history.rounds.some((round) => round.position !== null) ||
            comparisonHistory?.rounds.some((round) => round.position !== null))
            ? <ClassificationTrend data={history} compact teamName={nameOf(primary)}
              comparison={comparisonHistory ? { name: nameOf(secondary), data: comparisonHistory } : undefined} />
            : <p className="cs-muted">{t(primary ? 'comparison.noHistory' : 'comparison.choose')}</p>}
        </section>}
        <section className="pm-analysis-note">
          <h3>{t('analysisTitle')}</h3><p>{t('analysisText')}</p>
          <Button variant="text" endIcon={<ArrowForward />} onClick={openMatches}>{t('viewMatches')}</Button>
        </section>
      </aside>
    </div>}
    <footer className="pm-page-footer"><span>{t('navigationHint')}</span><span>Soccertycs / {t('pageTitle')}</span></footer>
  </div>;
}
